import { useEffect, useState } from "react";
import { getTeacherById } from "../api/teacher";

const teacherCache = new Map();
const teacherInflight = new Map();

function missingTeacherError() {
  return new Error("Teacher id is not configured");
}

function loadTeacherError(err) {
  return err instanceof Error ? err : new Error("Failed to load teacher");
}

function fetchTeacher(teacherId) {
  const cached = teacherCache.get(teacherId);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inflight = teacherInflight.get(teacherId);
  if (inflight) {
    return inflight;
  }

  const promise = getTeacherById(teacherId)
    .then((teacher) => {
      teacherCache.set(teacherId, teacher);
      return teacher;
    })
    .finally(() => {
      teacherInflight.delete(teacherId);
    });

  teacherInflight.set(teacherId, promise);
  return promise;
}

export function clearTeacherCache() {
  teacherCache.clear();
  teacherInflight.clear();
}

export function primeTeacherCache(teacherId, teacher) {
  if (teacherId && teacher) {
    teacherCache.set(teacherId, teacher);
    teacherInflight.delete(teacherId);
  }
}

export function useTeacher(teacherId) {
  const [state, setState] = useState(() => {
    if (!teacherId) {
      return { status: "error", error: missingTeacherError() };
    }
    const cachedTeacher = teacherCache.get(teacherId);
    if (cachedTeacher) {
      return { status: "success", teacher: cachedTeacher };
    }
    return { status: "loading" };
  });

  useEffect(() => {
    if (!teacherId) {
      setState({ status: "error", error: missingTeacherError() });
      return;
    }

    const cachedTeacher = teacherCache.get(teacherId);
    if (cachedTeacher) {
      setState({ status: "success", teacher: cachedTeacher });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    fetchTeacher(teacherId)
      .then((teacher) => {
        if (!cancelled) {
          setState({ status: "success", teacher });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            status: "error",
            error: loadTeacherError(err),
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [teacherId]);

  return state;
}
